import { useState, useCallback } from 'react';
import { useShop } from '../context/ShopContext';
import { useAuthenticatedFetch } from './useApi';

interface AiEditResult {
  html: string;
  subject?: string;
  tokensUsed?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

interface AiEditResponse {
  success: boolean;
  result?: AiEditResult;
  promptsUsedToday: number;
  promptsRemaining: number;
  error?: string;
}

interface AiUsageResponse {
  promptsUsedToday: number;
  promptsRemaining: number;
  dailyLimit: number;
  resetsAt: string;
}

interface UseAiTemplateEditorOptions {
  templateType?: string;
}

export function useAiTemplateEditor(options: UseAiTemplateEditorOptions = {}) {
  const { shop } = useShop();
  const fetchWithAuth = useAuthenticatedFetch();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<AiUsageResponse | null>(null);

  const fetchUsage = useCallback(async () => {
    if (!shop) return null;
    
    try {
      const response = await fetchWithAuth('/api/email/templates/ai-usage') as AiUsageResponse;
      setUsage(response);
      return response;
    } catch (err) {
      console.error('Error fetching AI usage:', err);
      return null;
    }
  }, [shop, fetchWithAuth]);

  const editWithAi = useCallback(async (
    prompt: string,
    currentHtml?: string,
    currentSubject?: string
  ): Promise<AiEditResult | null> => {
    if (!shop) {
      setError('Shop not configured');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth('/api/email/templates/ai-edit', {
        method: 'POST',
        body: {
          shop,
          templateType: options.templateType,
          prompt,
          currentHtml,
          currentSubject,
        },
      }) as AiEditResponse;

      if (!response.success) {
        setError(response.error || 'AI edit failed');
        return null;
      }

      // Update usage
      setUsage({
        promptsUsedToday: response.promptsUsedToday,
        promptsRemaining: response.promptsRemaining,
        dailyLimit: usage?.dailyLimit ?? 50,
        resetsAt: usage?.resetsAt ?? '',
      });

      return response.result || null;
    } catch (err: any) {
      const errorMessage = err?.body?.error || err?.message || 'AI edit failed';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [shop, options.templateType, fetchWithAuth, usage]);

  return {
    editWithAi,
    fetchUsage,
    isLoading,
    error,
    usage,
    clearError: () => setError(null),
  };
}
